import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ROOT_URL, API_V1_BASE_URL, S3_API_BASE_URL } from '../../config/api';
import { dataUrlToObjectUrl } from '../../utils/dataUrl';

interface SharedAsset {
    id: string;
    name: string;
    type: 'campaign' | 'document' | 'report' | 'dashboard';
    accessToken: string;
    accessUrl: string;
    expiresAt: string;
    isActive: boolean;
    sharedBy: string;
    sharedAt: string;
    thumbnail?: string;
    description?: string;
    imageKey?: string;
    fullImageUrl?: string;
}

interface GuestRecipient {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
}

const GuestDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [assets, setAssets] = useState<SharedAsset[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [userInfo, setUserInfo] = useState<any>(null);
    const [selectedAsset, setSelectedAsset] = useState<SharedAsset | null>(null);
    const [showVerification, setShowVerification] = useState(false);
    const [verificationStep, setVerificationStep] = useState<'email' | 'otp'>('email');
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
    const [timeLeft, setTimeLeft] = useState(300);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [currentToken, setCurrentToken] = useState<string | null>(null);
    const [campaignLoadError, setCampaignLoadError] = useState('');
    const [guestRecipients, setGuestRecipients] = useState<GuestRecipient[]>([]);
    const [recipientLoadError, setRecipientLoadError] = useState('');

    useEffect(() => {
        const user = localStorage.getItem('user');
        if (user) {
            const userData = JSON.parse(user);
            setUserInfo(userData);
            fetchGuestAssets();
            fetchGuestRecipients();
        } else {
            navigate('/login');
        }
    }, []);

    useEffect(() => {
        if (timeLeft <= 0) return;

        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    setMessage({ text: 'OTP has expired. Please request a new one.', type: 'error' });
                    setVerificationStep('email');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft, verificationStep]);

    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    const fetchGuestAssets = async () => {
        setIsLoading(true);
        setCampaignLoadError('');
        try {
            const accessToken = localStorage.getItem('accessToken');
            const response = await fetch(`${API_V1_BASE_URL}/guest/campaigns`, {
                headers: {
                    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
                }
            });
            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to load campaigns');
            }

            const campaigns: SharedAsset[] = (data.data || []).map((campaign: any) => ({
                id: String(campaign.campaign_id),
                name: campaign.title,
                type: 'campaign',
                accessToken: '',
                accessUrl: '',
                expiresAt: campaign.end_date || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
                isActive: String(campaign.status || '').toLowerCase() !== 'inactive',
                sharedBy: 'Your organization',
                sharedAt: campaign.updated_at || campaign.start_date || new Date().toISOString(),
                description: campaign.description || '',
                imageKey: campaign.image_url || undefined,
                fullImageUrl: campaign.image_url || campaign.video_url || undefined
            }));

            setAssets(campaigns);
        } catch (error) {
            console.error('Error fetching guest assets:', error);
            setAssets([]);
            setCampaignLoadError(error instanceof Error ? error.message : 'Failed to load campaigns');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchGuestRecipients = async () => {
        setRecipientLoadError('');
        try {
            const accessToken = localStorage.getItem('accessToken');
            const response = await fetch(`${API_V1_BASE_URL}/guest/recipients`, {
                headers: {
                    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
                }
            });
            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to load guest users');
            }

            setGuestRecipients(data.data || []);
        } catch (error) {
            console.error('Error fetching guest recipients:', error);
            setGuestRecipients([]);
            setRecipientLoadError(error instanceof Error ? error.message : 'Failed to load guest users');
        }
    };

    const getAssetIcon = (type: string) => {
        switch (type) {
            case 'campaign': return '📢';
            case 'document': return '📄';
            case 'report': return '📊';
            case 'dashboard': return '📈';
            default: return '🔗';
        }
    };

    const handleAssetClick = (asset: SharedAsset) => {
        setSelectedAsset(asset);
        setShowVerification(true);
        setVerificationStep('email');
        setOtp('');
        setEmail('');
        setMessage(null);
        setTimeLeft(300);
        setCurrentToken(null);
    };

    const handleSendOTP = async () => {
        if (!email) {
            setMessage({ text: 'Please select a guest user', type: 'error' });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setMessage({ text: 'Please enter a valid email address', type: 'error' });
            return;
        }

        setIsVerifying(true);
        setMessage(null);

        try {
            const imageIdentifier = selectedAsset?.fullImageUrl;

            if (!imageIdentifier) {
                setMessage({ text: 'This campaign does not have an image or video configured for OTP access.', type: 'error' });
                setIsVerifying(false);
                return;
            }

            console.log('Generating access with:', {
                imageIdentifier: imageIdentifier,
                expirationMinutes: 30,
                email: email
            });

            // Generate presigned URL
            const generateResponse = await fetch(`${S3_API_BASE_URL}/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    imageIdentifier: imageIdentifier,
                    expirationMinutes: 30
                })
            });

            const generateResult = await generateResponse.json();

            if (!generateResponse.ok) {
                setMessage({ text: `✗ Failed to generate access link: ${generateResult.message}`, type: 'error' });
                setIsVerifying(false);
                return;
            }

            // Extract token from the presigned URL
            const urlParts = generateResult.presignedUrl.split('/');
            const token = urlParts[urlParts.length - 1];
            setCurrentToken(token);

            // Send OTP
            const response = await fetch(`${S3_API_BASE_URL}/send-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`
                },
                body: JSON.stringify({
                    token: token,
                    email: email,
                    campaignId: selectedAsset?.id
                })
            });

            const result = await response.json();

            if (response.ok) {
                setMessage({ text: `✓ Secure campaign access was sent to ${email}.`, type: 'success' });
                setTimeout(() => {
                    setShowVerification(false);
                    setSelectedAsset(null);
                    setMessage(null);
                    setEmail('');
                    setCurrentToken(null);
                }, 2500);
            } else {
                setMessage({ text: `✗ ${result.message || 'Failed to send secure campaign access'}`, type: 'error' });
            }
        } catch (error) {
            console.error('Error in OTP flow:', error);
            setMessage({ text: '✗ Failed to send secure campaign access', type: 'error' });
        } finally {
            setIsVerifying(false);
        }
    };

    const handleVerifyOTP = async () => {
        if (!otp || otp.length !== 6) {
            setMessage({ text: 'Please enter the 6-digit verification code', type: 'error' });
            return;
        }

        if (!currentToken) {
            setMessage({ text: 'Access token not found. Please request OTP again.', type: 'error' });
            return;
        }

        setIsVerifying(true);
        setMessage(null);

        try {
            const response = await fetch(`${S3_API_BASE_URL}/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token: currentToken,
                    otp: otp
                })
            });

            const result = await response.json();

            if (response.ok && result.url) {
                if (String(result.url).startsWith('data:')) {
                    const mediaUrl = dataUrlToObjectUrl(result.url);
                    window.open(mediaUrl, '_blank');
                    window.setTimeout(() => URL.revokeObjectURL(mediaUrl), 60_000);
                } else {
                    window.open(result.url, '_blank');
                }
                setMessage({ text: '✓ Access granted! Opening campaign content in new tab...', type: 'success' });

                // Record access log
                try {
                    await fetch(`${API_ROOT_URL}/guest/record-access`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            token: currentToken,
                            assetId: selectedAsset?.id,
                            campaignName: selectedAsset?.name,
                            email: email,
                            action: 'view_access',
                            accessedAt: new Date().toISOString()
                        })
                    });
                } catch (logError) {
                    console.error('Error recording access:', logError);
                }

                setTimeout(() => {
                    setShowVerification(false);
                    setSelectedAsset(null);
                    setMessage(null);
                    setVerificationStep('email');
                    setOtp('');
                    setCurrentToken(null);
                }, 2000);
            } else {
                setMessage({ text: `✗ ${result.message || 'Invalid verification code'}`, type: 'error' });
            }
        } catch (error) {
            console.error('Verification error:', error);
            setMessage({ text: '✗ Verification failed. Please try again.', type: 'error' });
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResendOTP = () => {
        if (resendCooldown > 0) return;
        setResendCooldown(30);
        handleSendOTP();
    };

    const handleLogout = () => {
        localStorage.removeItem('user');
        navigate('/login');
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getDaysLeft = (expiresAt: string) => {
        const expiry = new Date(expiresAt);
        const now = new Date();
        const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return daysLeft;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading your campaigns...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            {/* Header */}
            <div className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className="text-3xl">🎬</div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-800">Movie Campaigns Portal</h1>
                                <p className="text-sm text-gray-500">Welcome, {userInfo?.email}</p>
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="px-4 py-2 text-gray-600 hover:text-gray-800 flex items-center gap-2"
                        >
                            <span>🚪</span> Logout
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Welcome Banner */}
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 mb-8 text-white">
                    <h2 className="text-2xl font-bold mb-2">🎬 Movie Marketing Campaigns</h2>
                    <p className="text-purple-100">
                        Access exclusive movie campaign assets, posters, trailers, and analytics.
                        Click on any campaign to request access. You'll need to verify your email for security.
                    </p>
                </div>

                {/* Campaigns Grid */}
                {campaignLoadError && (
                    <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                        {campaignLoadError}
                    </div>
                )}
                {assets.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-12 text-center">
                        <div className="text-6xl mb-4">🎬</div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">No Campaigns Available</h3>
                        <p className="text-gray-600">
                            There are no campaigns for your organization.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {assets.map(asset => {
                            const daysLeft = getDaysLeft(asset.expiresAt);
                            const isExpiring = daysLeft <= 3;
                            const isExpired = daysLeft <= 0;

                            return (
                                <div
                                    key={asset.id}
                                    onClick={() => !isExpired && handleAssetClick(asset)}
                                    className={`bg-white rounded-xl shadow-md transition-all overflow-hidden group ${!isExpired ? 'hover:shadow-xl cursor-pointer hover:scale-105 transform transition-all duration-300' : 'opacity-60 cursor-not-allowed'
                                        }`}
                                >
                                    <div className="relative h-48 bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                                        <div className="text-7xl group-hover:scale-110 transition-transform">
                                            🎬
                                        </div>
                                        <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity"></div>
                                    </div>
                                    <div className="p-6">
                                        <div className="text-5xl mb-4 group-hover:scale-110 transition-transform">
                                            {getAssetIcon(asset.type)}
                                        </div>
                                        <h3 className="font-semibold text-lg text-gray-800 mb-2">
                                            {asset.name}
                                        </h3>
                                        {asset.description && (
                                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                                {asset.description}
                                            </p>
                                        )}
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-xs px-2 py-1 bg-gray-100 rounded-full capitalize">
                                                {asset.type}
                                            </span>
                                            {isExpired ? (
                                                <span className="text-xs text-red-600">Expired</span>
                                            ) : (
                                                <span className={`text-xs ${isExpiring ? 'text-orange-600' : 'text-green-600'}`}>
                                                    {daysLeft} days left
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            Shared by: {asset.sharedBy}
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            Shared on: {formatDate(asset.sharedAt)}
                                        </div>
                                    </div>
                                    <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
                                        <button
                                            className={`w-full font-medium text-sm ${isExpired
                                                ? 'text-gray-400 cursor-not-allowed'
                                                : 'text-purple-600 hover:text-purple-700'
                                                }`}
                                            disabled={isExpired}
                                        >
                                            {isExpired ? 'Access Expired' : '🎯 Access Campaign →'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Verification Modal */}
            {showVerification && selectedAsset && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-800">Share Secure Campaign</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {selectedAsset.name}
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    setShowVerification(false);
                                    setSelectedAsset(null);
                                    setMessage(null);
                                    setVerificationStep('email');
                                    setCurrentToken(null);
                                }}
                                className="text-gray-400 hover:text-gray-600 text-2xl"
                            >
                                ×
                            </button>
                        </div>

                        <div className="p-6">
                            {/* Campaign Info */}
                            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg flex items-center gap-3">
                                <span className="text-4xl">🎬</span>
                                <div>
                                    <p className="font-semibold text-gray-800">{selectedAsset.name}</p>
                                    <p className="text-xs text-gray-500">Campaign Access</p>
                                </div>
                            </div>

                            {/* Timer Display for OTP Step */}
                            {verificationStep === 'otp' && (
                                <div className="mb-4 p-3 bg-gray-50 rounded-lg text-center">
                                    <span className="text-sm font-medium text-gray-600">
                                        ⏱️ Code expires in: <span className="font-bold text-purple-600">{formatTime(timeLeft)}</span>
                                    </span>
                                </div>
                            )}

                            {/* Messages */}
                            {message && (
                                <div className={`mb-4 p-3 rounded-lg ${message.type === 'success'
                                    ? 'bg-green-50 text-green-800 border border-green-200'
                                    : 'bg-red-50 text-red-800 border border-red-200'
                                    }`}>
                                    {message.text}
                                </div>
                            )}

            {/* Guest recipient step */}
                            {verificationStep === 'email' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Share with Guest User
                                        </label>
                                        <select
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
                                            autoFocus
                                        >
                                            <option value="">Select a guest user</option>
                                            {guestRecipients.map(recipient => {
                                                const fullName = [recipient.first_name, recipient.last_name].filter(Boolean).join(' ');
                                                return (
                                                    <option key={recipient.id} value={recipient.email}>
                                                        {fullName ? `${fullName} — ${recipient.email}` : recipient.email}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        <p className="text-xs text-gray-500 mt-1">
                                            The secure link and verification code will be sent to this guest user.
                                        </p>
                                        {recipientLoadError && (
                                            <p className="text-xs text-red-600 mt-2">{recipientLoadError}</p>
                                        )}
                                        {!recipientLoadError && guestRecipients.length === 0 && (
                                            <p className="text-xs text-amber-600 mt-2">No active guest users exist for this organization.</p>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleSendOTP}
                                        disabled={isVerifying || !email}
                                        className={`w-full py-2 rounded-lg font-semibold transition ${isVerifying || !email
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:shadow-lg text-white'
                                            }`}
                                    >
                                        {isVerifying ? 'Sending...' : 'Send Secure Campaign Access'}
                                    </button>
                                </div>
                            )}

                            {/* OTP Step */}
                            {verificationStep === 'otp' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Verification Code
                                        </label>
                                        <input
                                            type="text"
                                            maxLength={6}
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                            placeholder="Enter 6-digit code"
                                            className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500 text-center text-2xl tracking-widest"
                                            autoFocus
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Enter the 6-digit code sent to {email}
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleVerifyOTP}
                                        disabled={isVerifying}
                                        className={`w-full py-2 rounded-lg font-semibold transition ${isVerifying
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:shadow-lg text-white'
                                            }`}
                                    >
                                        {isVerifying ? 'Verifying...' : 'Verify & Access Campaign'}
                                    </button>
                                    <button
                                        onClick={handleResendOTP}
                                        disabled={resendCooldown > 0 || isVerifying}
                                        className={`w-full py-2 rounded-lg font-semibold transition border-2 ${resendCooldown > 0 || isVerifying
                                            ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                                            : 'border-purple-600 text-purple-600 hover:bg-purple-50'
                                            }`}
                                    >
                                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                            <p className="text-xs text-gray-500 text-center">
                                🔒 Your identity will be verified before granting access to campaign assets.
                                All access attempts are logged for security.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GuestDashboard;
